/* eslint-disable eqeqeq */
/* eslint-disable no-console */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import CustomLayout from "../../../Core/Layout/CustomLayout";
import { INITIAL_ZOOM_LEVEL, MAX_BOUNDS } from "../../Home/Data/HomeData";
import { ExpandButton } from "../../../Core/Components/MapMarkerButtons";
import getStatus from "../getStatus";
import KPIs from "./KPIs";
import VehicleInformation from "./VehicleInformation";
import EnergyConsumption from "./EnergyConsumption";
import SafetyChecklistModal from "./SafetyChecklistModal";
import { updateRental } from "../../../Redux/Slices/rentals.slice";
import MapBottomCard from "./MapBottomCard";
import FullscreenMap from "./FullscreenMap";
import { client } from "../../../Utils/axiosClient";
import { RENTAL_STATUS } from "../constant";
import getMapIcon from "./getMapIcon";
import { DEFAULT_LAT, DEFAULT_LONG } from "../../Login/constants";
import { getTrikeBreakdownByTrip } from "../../../Redux/APIs/trikeBreakdownAPI";
import { getBatterySosByTrip } from "../../../Redux/APIs/batterySosAPI";
import TrikeRepairDrawer, {
  trikeRepairSteps,
} from "../../Trips/TripDetails/TrikeRepairDrawer";
import BatterySwapDrawer from "../../Trips/TripDetails/BatterySwapDrawer";
import TripRequests from "../../Trips/phase3/TripRequests";
import { batterySwapSteps } from "../../Trips/TripDetails/constants";
import { updateTrips } from "../../../Redux/Slices/trips.slice";

const RentalDetailScreen = () => {
  const { id } = useParams();

  const dispatch = useDispatch();

  const { currentRental, checklistModal } = useSelector(
    (state) => state.rentals,
  );

  const { batterySwapRequestModal, trikeRepairModal } = useSelector(
    (state) => state.trips,
  );

  const { userDetails } = useSelector((state) => state.loggedInUser);

  const [details, setDetails] = useState({});
  const [fullScreenMap, setFullScreenMap] = useState(false);

  const mapRef = useRef(null);

  const getAssetLatLngs = () => {
    const baseLat = parseFloat(userDetails?.baseLat || DEFAULT_LAT);
    const baseLong = parseFloat(userDetails?.baseLong || DEFAULT_LONG);

    if (currentRental?.lat && currentRental?.long) {
      const coords = [
        parseFloat(currentRental.lat),
        parseFloat(currentRental.long),
      ];

      return coords;
    }

    // return [-0.6803741, 34.7569482];
    return [baseLat, baseLong];
  };

  const getDetails = useCallback(async () => {
    try {
      dispatch(updateRental({ key: "loading", value: true }));

      const response = await client.get(
        `${process.env.REACT_APP_API_URL}/rentals/rental-details-by-id`,
        { params: { rentalId: +id } },
      );

      if (response.status === 200) {
        const value = response?.data?.result || {};
        setDetails({ ...value });
        dispatch(updateRental({ key: "currentRental", value }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(updateRental({ key: "loading", value: false }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      dispatch(getTrikeBreakdownByTrip({ rentalId: +id }));
      dispatch(getBatterySosByTrip({ rentalId: +id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    let timeout;

    if (id) {
      if (currentRental?.status == RENTAL_STATUS.ONGOING) {
        timeout = setTimeout(() => {
          getDetails();
        }, 5000);
      } else {
        getDetails();
      }
    }

    return () => clearTimeout(timeout);
  }, [getDetails, id, currentRental?.status]);

  return (
    <CustomLayout pageName="Rentals" nestedName={`${id}`} hideFooter>
      <Row className="w-full h-full">
        <Col span={16}>
          <div className="w-full h-full bg-background-white">
            <div className="flex items-center justify-between py-4 px-6 h-14 border-b border-light-grey">
              <p className="font-poppins font-medium text-base text-black">
                Overview
              </p>

              <div>{getStatus(currentRental)}</div>
            </div>

            <div className="flex flex-col gap-y-6 p-6 w-full h-[calc(100vh-72px-56px)] overflow-y-scroll bg-background">
              <KPIs details={details} />
              <TripRequests />
              <VehicleInformation details={details} />
              <EnergyConsumption details={details} />
            </div>
          </div>
        </Col>

        <Col span={8}>
          <div className="py-4 px-6 h-14 border-b border-l border-light-grey bg-background-white">
            <p className="font-poppins font-medium text-base text-black">
              Live Location
            </p>
          </div>

          <div className="border-l border-light-grey">
            <div className="h-[calc(100vh-128px)]">
              <MapContainer
                center={getAssetLatLngs()}
                zoom={INITIAL_ZOOM_LEVEL}
                maxBounds={MAX_BOUNDS}
                className="z-10 w-full h-full"
                zoomControl={false}
                ref={mapRef}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                <Marker position={getAssetLatLngs()} icon={getMapIcon()} />
              </MapContainer>
            </div>

            <div className="absolute z-20 w-full left-6 top-20">
              <div className="flex items-center justify-between">
                <div />

                <div className="flex mr-14 gap-x-4">
                  <ExpandButton onClick={() => setFullScreenMap(true)} />
                </div>
              </div>
            </div>

            <div className="z-20">
              <MapBottomCard
                mapRef={mapRef}
                initialZoomLevel={INITIAL_ZOOM_LEVEL}
                center={getAssetLatLngs()}
              />
            </div>
          </div>
        </Col>

        <FullscreenMap
          visible={fullScreenMap}
          onCancel={() => setFullScreenMap(false)}
        />
        <SafetyChecklistModal
          data={details?.checklist || []}
          visible={checklistModal?.visible}
          onCancel={() =>
            dispatch(
              updateRental({
                key: "checklistModal",
                value: {
                  visible: false,
                  data: {},
                },
              }),
            )
          }
        />

        <BatterySwapDrawer
          visible={batterySwapRequestModal?.visible}
          isRental
          rentalId={id}
          onCancel={() => {
            let nextStep = 0;
            if (
              batterySwapRequestModal?.data?.step ===
              batterySwapSteps.REQUEST_ACCEPTED_AGENT
            ) {
              nextStep = batterySwapSteps.REQUEST_COMPLETED_AGENT;
            }
            dispatch(
              updateTrips({
                key: "batterySwapRequestModal",
                value: { visible: false, data: { step: nextStep } },
              }),
            );
          }}
        />
        <TrikeRepairDrawer
          visible={trikeRepairModal?.visible}
          isRental
          rentalId={id}
          onCancel={() => {
            const nextStep =
              trikeRepairModal?.data?.step === trikeRepairSteps.REQUEST_ACCEPTED
                ? trikeRepairSteps.REQUEST_COMPLETED
                : trikeRepairSteps.REQUEST_REJECTED;
            dispatch(
              updateTrips({
                key: "trikeRepairModal",
                value: { visible: false, data: { step: nextStep } },
              }),
            );
          }}
        />
      </Row>
    </CustomLayout>
  );
};

export default RentalDetailScreen;
